// The merge behind Rules → Companies → Uploaders: one row per
// address-or-domain, carrying the THREE facts a document's inbound path can
// depend on — whether the sender may email documents into intake
// (`intake_senders`), which company an upload or email from it files to
// (`company_email_domains`, "the uploader-domain rung"; a full address may
// route too since D-379, and wins over its domain), and whether its documents'
// format is set rather than guessed at extraction (`sender_format_defaults`,
// D-379). Pure and testable on purpose: screens do not talk to the transport,
// and the mock harness can only reach `src/lib`.
//
// The join is EXACT-STRING, never widened. `jane@x.test` (an address, checked
// by the intake gate) and `@x.test` (a domain, checked by the company-routing
// rung) are two different comparisons in the backend and stay two different
// entries here — collapsing them would imply a relationship neither rung
// actually has.
//
// BOTH TABLES ALLOW MANY ROWS PER KEY. The unique indexes on both are partial
// (`WHERE is_active`), so retiring a row frees its string for a brand-new row
// with a different id (`app/mock/tests/intake-senders.test.ts` asserts this
// for intake senders; the same partial-index shape holds for domains). A
// `SenderEntry` that collapsed each side to one nullable row would silently
// drop whichever row the join didn't keep, and could hand a caller a retired
// row's id/`row_version` to PATCH — which would either land on dead data or,
// worse, come back a raw uniqueness violation. So each side is AT MOST ONE
// active row (`intake` / `routing`) plus every other row for that key
// (`intakeHistory` / `routingHistory`).
//
// WRITES ARE ONE CALL (code review G-01, D-275). Every save — a new rule, an
// edit, Inactive, Active — is one `PUT /sender-rules`, which writes both rows
// in one server transaction; `statusRequest` and `clauseRequest` below build
// it. The screen no longer makes two to four separate calls.
import {
  DOCUMENT_FORM,
  dormantSenderRows,
  normalizeSenderKey,
  type DocumentForm,
  type SenderKeyKind,
} from '@ap/shared'
import type { CompanyEmailDomain, IntakeSender, SenderFormatDefault } from './types'
import type { SenderRulePut } from '../api/types'
import { closeSentence } from './format'

export interface SenderEntry {
  key: string
  isDomain: boolean
  intake: IntakeSender | null
  intakeHistory: IntakeSender[]
  routing: CompanyEmailDomain | null
  routingHistory: CompanyEmailDomain[]
  /** The format default (D-379): no Active row is "is interpreted". */
  format: SenderFormatDefault | null
  formatHistory: SenderFormatDefault[]
  /**
   * AN INACTIVE ENTRY'S RULE, as it stood when it went Inactive (QA wave B,
   * 2026-09-25, B-01; `docs/decisions.md` D-167). Both `null` on an Active
   * entry. These are the rows `PUT /sender-rules` puts back on Active, decided
   * by the same `dormantSenderRows` the server runs: the rows made Inactive in
   * the same write, which carry the same `retired_at` exactly.
   */
  dormant: { intake: IntakeSender | null; routing: CompanyEmailDomain | null; format: SenderFormatDefault | null }
}

/** Merge the two masters on their exact stored string. Deterministic order —
    `key.localeCompare` — so the tab's row order doesn't shuffle on reload.
    No filtering: whether a retired entry is worth showing is the caller's
    business (the Senders tab's "Show retired" switch). */
export function mergeSenderEntries(
  senders: readonly IntakeSender[],
  domains: readonly CompanyEmailDomain[],
  formats: readonly SenderFormatDefault[] = [],
): SenderEntry[] {
  const byKey = new Map<string, SenderEntry>()
  const entryFor = (key: string): SenderEntry => {
    let e = byKey.get(key)
    if (!e) {
      e = {
        key,
        isDomain: key.startsWith('@'),
        intake: null,
        intakeHistory: [],
        routing: null,
        routingHistory: [],
        format: null,
        formatHistory: [],
        dormant: { intake: null, routing: null, format: null },
      }
      byKey.set(key, e)
    }
    return e
  }
  for (const s of senders) {
    const e = entryFor(s.address)
    if (s.is_active) e.intake = s
    else e.intakeHistory.push(s)
  }
  for (const d of domains) {
    const e = entryFor(d.domain)
    if (d.is_active) e.routing = d
    else e.routingHistory.push(d)
  }
  for (const f of formats) {
    const e = entryFor(f.key)
    if (f.is_active) e.format = f
    else e.formatHistory.push(f)
  }
  for (const e of byKey.values()) {
    e.dormant = dormantSenderRows(
      [...(e.intake ? [e.intake] : []), ...e.intakeHistory],
      [...(e.routing ? [e.routing] : []), ...e.routingHistory],
      [...(e.format ? [e.format] : []), ...e.formatHistory],
    )
  }
  return [...byKey.values()].sort((a, b) => a.key.localeCompare(b.key))
}

/** The intake row the entry's RULE holds: the active one, or on an Inactive
    entry the one Active would put back. What the sentence and the editor read. */
export function ruleIntake(e: SenderEntry): IntakeSender | null {
  return e.intake ?? e.dormant.intake
}

/** The routing row the entry's rule holds — see `ruleIntake`. */
export function ruleRouting(e: SenderEntry): CompanyEmailDomain | null {
  return e.routing ?? e.dormant.routing
}

/** The format the entry's rule sets, or `null`: interpreted, pass 1's guess stands (D-378). */
export function ruleFormat(e: SenderEntry): DocumentForm | null {
  return (e.format ?? e.dormant.format)?.document_form ?? null
}

/** What this screen saw as each side's Active row: the versions half of
    every `PUT /sender-rules`. A rule that moved since is a 409. */
function seenVersions(e: SenderEntry | null): Pick<
  SenderRulePut,
  'intake_sender_row_version' | 'company_email_domain_row_version' | 'sender_format_default_row_version'
> {
  return {
    intake_sender_row_version: e?.intake?.row_version ?? null,
    company_email_domain_row_version: e?.routing?.row_version ?? null,
    sender_format_default_row_version: e?.format?.row_version ?? null,
  }
}

/**
 * A STATUS CHANGE, as one request — the editor's Status and the bulk strip
 * both send this, so they cannot mean different things by Active or Inactive.
 * `null` when there is nothing to change: Inactive on an entry with no Active
 * row, or Active on one with nothing to put back. A caller must say so rather
 * than report success (B-01).
 */
export function statusRequest(e: SenderEntry, active: boolean): SenderRulePut | null {
  if (active === entryIsActive(e)) return null
  if (active && e.dormant.intake === null && e.dormant.routing === null && e.dormant.format === null) return null
  return { key: e.key, is_active: active, ...seenVersions(e) }
}

/** A new rule or an edit of its clauses, as one request. A single address
    routes as a domain does since D-379; `documentForm` `null` is "is
    interpreted". */
export function clauseRequest(
  key: string,
  existing: SenderEntry | null,
  clauses: { kind: SenderKeyKind; intakeAllowed: boolean; companyId: string | null; documentForm: DocumentForm | null },
): SenderRulePut {
  return {
    key,
    is_active: true,
    key_kind: clauses.kind,
    intake_allowed: clauses.intakeAllowed,
    company_id: clauses.companyId,
    document_form: clauses.documentForm,
    ...seenVersions(existing),
  }
}

/** An entry is "active" if any side of it is — a sender allowed to email in
    but not routed anywhere is still active, and so is a domain routed
    somewhere but not allowed to email in, or one whose format is set. */
export function entryIsActive(e: SenderEntry): boolean {
  return e.intake !== null || e.routing !== null || e.format !== null
}

/** The sentence's first choice, in the owner's words (D-379). */
export const SENDER_KIND_WORDS: Readonly<Record<SenderKeyKind, string>> = {
  address: 'a specific user',
  domain: 'a general domain',
}

/** The format clause's three answers, in the owner's words (D-379); `interpreted` is no stored row. */
export const FORMAT_WORDS: Readonly<Record<DocumentForm | 'interpreted', string>> = {
  [DOCUMENT_FORM.Electronic]: 'defaults to electronic',
  [DOCUMENT_FORM.Paper]: 'defaults to paper',
  interpreted: 'is interpreted',
}

/**
 * THE RULE AS ONE SENTENCE, the table's and the editor's alike, in the owner's
 * words (2026-10-19 — D-379):
 *
 *   When an invoice is uploaded from a specific user
 *   SLJDocumentScan@seniorlifejohnstown.com, intake is allowed, routing goes to
 *   SeniorLIFE Johnstown and format defaults to paper.
 *
 * THE RULE's clauses, so an Inactive entry reads as what Active would put back
 * (B-01). Every clause is always said: an address routes as a domain does, and
 * "None" is a real answer.
 */
export function uploaderRuleSentence(e: SenderEntry, companyNames: ReadonlyMap<string, string>): string {
  const who = SENDER_KIND_WORDS[e.isDomain ? 'domain' : 'address']
  const intake = ruleIntake(e) ? 'allowed' : 'not allowed'
  const company = routedCompanyName(e, companyNames) ?? 'None'
  const format = FORMAT_WORDS[ruleFormat(e) ?? 'interpreted']
  return closeSentence(
    `When an invoice is uploaded from ${who} ${e.key}, intake is ${intake}, routing goes to ${company} and format ${format}`,
  )
}

/**
 * The company an entry's routing resolves to, by NAME — or `null` when its
 * rule routes nowhere.
 *
 * The MAP WINS over the row's own `company_name`: the copied name is refreshed
 * on a re-point since D-275, but a company RENAMED in References leaves every
 * copy behind, and the map has the current name.
 */
export function routedCompanyName(e: SenderEntry, companyNames: ReadonlyMap<string, string>): string | null {
  // The RULE's routing, so an Inactive entry still names the company Active
  // would route to again (B-01).
  const routing = ruleRouting(e)
  if (routing === null) return null
  return companyNames.get(routing.company_id) ?? routing.company_name ?? routing.company_id
}

/**
 * WHAT THE EDITOR SAYS UNDER THE KEY FIELD (QA 2026-10-04 A-01): the stored key
 * when normalization would change what was typed — case and surrounding spaces
 * included, which the old test compared away — and `null` when it would not.
 */
export function storedKeyNotice(typed: string): string | null {
  if (typed.trim() === '') return null
  const stored = normalizeSenderKey(typed)
  return stored === typed ? null : stored
}

/** The Active entry a NEW rule's key would collide with, before Save (A-02). */
export function activeEntryFor(entries: readonly SenderEntry[] | null, typed: string): SenderEntry | null {
  const key = normalizeSenderKey(typed)
  if (key === '' || entries === null) return null
  return entries.find((e) => e.key === key && entryIsActive(e)) ?? null
}
